/*
 * aw-section-fixes.js - Alive Water, 2026-07-26 (theme E order page)
 *
 * Repairs three sections that shipped non-functional. Owned by Alive Water;
 * theme.js and the developer's shared assets are not touched.
 *
 *  1. testimonials ("Why choose Alive Spring Water delivery?")
 *     theme.Testimonials hardcodes its Flickity options
 *     ({adaptiveHeight, avoidReflow, pageDots:true, prevNextButtons:false}) and
 *     never reads the section's own Slider settings, so "Show arrows",
 *     "Arrow color", "Autoplay", "Autoplay speed" and "Show dots" did nothing.
 *     We attach arrows/autoplay to the live Flickity instance instead of
 *     re-initialising it, so the theme editor hooks (onBlockSelect etc.) keep
 *     working.
 *
 *  2. video-slider ("What Our Customers Are Saying...")
 *     The section emitted pre-rendered Swiper DOM copied out of the old React
 *     build - swiper-initialized, swiper-cube, seven hardcoded bullets - but
 *     Swiper itself is never loaded on this template. Nothing moved and slides
 *     2-7 sat off to the right. We drive the same markup directly; no library.
 *
 *  3. product-3d-card (the "Glass Dispensers and Shelves" cards)
 *     <model-viewer> was never a defined custom element on this page (Shopify's
 *     model-viewer-ui bundle supplies Shopify.ModelViewerUI but NOT the element,
 *     verified 2026-07-26), and src ran the raw .glb URL through | image_url,
 *     which is a Liquid error. The element is now loaded on demand, on the
 *     first click of a card - no cost to anyone who does not open a model.
 */
(function () {
  'use strict';

  if (window.__awSectionFixes) return;
  window.__awSectionFixes = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ================================================================== *
   * 1. Testimonials carousel - arrows + autoplay
   * ================================================================== */

  var ARROW_SVG = {
    prev: '<svg aria-hidden="true" focusable="false" viewBox="0 0 284.49 498.98"><path d="M249.49 0a35 35 0 0 1 24.75 59.75L84.49 249.49l189.75 189.74a35.002 35.002 0 1 1-49.5 49.5L10.25 274.24a35 35 0 0 1 0-49.5L224.74 10.25A34.89 34.89 0 0 1 249.49 0z"/></svg>',
    next: '<svg aria-hidden="true" focusable="false" viewBox="0 0 284.49 498.98"><path d="M35 498.98a35 35 0 0 1-24.75-59.75l189.74-189.74L10.25 59.75a35.002 35.002 0 0 1 49.5-49.5l214.49 214.49a35 35 0 0 1 0 49.5L59.75 488.73A34.89 34.89 0 0 1 35 498.98z"/></svg>'
  };

  // theme.js boots Flickity lazily (theme.initWhenVisible, 600px threshold), so
  // the instance does not exist at DOMContentLoaded. Poll, then give up quietly.
  function whenFlickity(slider, cb) {
    var tries = 0;
    (function poll() {
      var fk = window.Flickity && window.Flickity.data ? window.Flickity.data(slider) : null;
      if (fk) return cb(fk);
      if (++tries > 300) return; // ~60s
      setTimeout(poll, 200);
    })();
  }

  function buildArrow(dir, color) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aw-arrow aw-arrow--' + dir;
    btn.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
    btn.innerHTML = ARROW_SVG[dir];
    if (color) btn.style.color = color;
    return btn;
  }

  function initTestimonials(section) {
    var slider = section.querySelector('.testimonials-slider');
    if (!slider || slider.getAttribute('data-aw-wired') === 'true') return;
    slider.setAttribute('data-aw-wired', 'true');

    var wantArrows = section.getAttribute('data-aw-arrows') === 'true';
    var arrowColor = section.getAttribute('data-aw-arrow-color') || '';
    var autoplay = section.getAttribute('data-aw-autoplay') === 'true';
    var speed = parseFloat(section.getAttribute('data-aw-autoplay-speed') || '7') * 1000;
    if (!(speed > 0)) speed = 7000;

    whenFlickity(slider, function (fk) {
      var wrapper = slider.parentNode;

      // Flickity measures every cell once. Cards whose illustration is an <img>
      // rather than inline SVG can still be settling at that moment, so
      // adaptiveHeight locks the viewport to a height smaller than the finished
      // card and the description gets clipped (observed: 437px viewport vs a
      // 533px card, cutting the last lines off the Vortexer and Find a Spring
      // cards). The snippets now reserve space with aspect-ratio, and this
      // re-measures if anything still lands late.
      var settle = function () { try { fk.resize(); } catch (e) {} };
      each(slider.querySelectorAll('img'), function (im) {
        if (im.complete && im.naturalWidth) return;
        im.addEventListener('load', settle, { once: true });
        im.addEventListener('error', settle, { once: true });
      });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', settle, { once: true });
      }
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(settle);
      }

      // Those load hooks still lose the race when a lazy illustration only
      // decodes as its card scrolls into view: the cached cell height stays
      // short and the card is clipped. Re-measure the first time each card is
      // shown - once per card, so resize() cannot feed itself. This has to be
      // 'settle' rather than 'select': Flickity ignores resize() while it is
      // animating, so a re-measure during the transition does nothing.
      var remeasured = {};
      fk.on('settle', function () {
        var i = fk.selectedIndex;
        if (remeasured[i]) return;
        remeasured[i] = true;
        settle();
      });

      if (wantArrows && fk.slides && fk.slides.length > 1 && !wrapper.querySelector('.aw-arrow')) {
        var prev = buildArrow('prev', arrowColor);
        var next = buildArrow('next', arrowColor);
        prev.addEventListener('click', function () { stopAuto(); fk.previous(); });
        next.addEventListener('click', function () { stopAuto(); fk.next(); });
        wrapper.appendChild(prev);
        wrapper.appendChild(next);
      }

      // Clicking a side card should bring it to the front. Links inside a card
      // still behave as links.
      fk.on('staticClick', function (event, pointer, cellElement, cellIndex) {
        if (typeof cellIndex !== 'number') return;
        if (event.target && event.target.closest && event.target.closest('a')) return;
        if (cellIndex === fk.selectedIndex) return;
        stopAuto();
        fk.select(cellIndex);
      });

      /* --- autoplay ------------------------------------------------- */
      var timer = null;
      var stopped = false;

      function tick() {
        if (document.hidden) return;
        fk.next();
      }
      function startAuto() {
        if (stopped || timer || reduceMotion) return;
        timer = setInterval(tick, speed);
      }
      function pauseAuto() {
        if (timer) { clearInterval(timer); timer = null; }
      }
      function stopAuto() {
        stopped = true;
        pauseAuto();
      }

      if (autoplay && !reduceMotion && fk.slides && fk.slides.length > 1) {
        section.addEventListener('mouseenter', pauseAuto);
        section.addEventListener('mouseleave', startAuto);
        section.addEventListener('focusin', pauseAuto);
        section.addEventListener('focusout', startAuto);
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) pauseAuto(); else startAuto();
        });
        fk.on('dragStart', stopAuto);
        startAuto();
      }
    });
  }

  /* ================================================================== *
   * 2. Video testimonials slider
   * ================================================================== */

  function initVideoSlider(root) {
    if (root.getAttribute('data-aw-wired') === 'true') return;
    root.setAttribute('data-aw-wired', 'true');

    var frame = root.querySelector('.swiper');
    var track = root.querySelector('.swiper-wrapper');
    if (!frame || !track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('.swiper-slide'));
    if (!slides.length) return;

    var prevBtn = root.querySelector('.swiper-button-prev');
    var nextBtn = root.querySelector('.swiper-button-next');
    var bullets = Array.prototype.slice.call(root.querySelectorAll('.swiper-pagination-bullet'));
    var index = 0;

    function slideWidth() {
      return frame.getBoundingClientRect().width || slides[0].getBoundingClientRect().width;
    }

    function stopVideos(except) {
      each(root.querySelectorAll('video'), function (v) {
        if (v === except) return;
        try { v.pause(); v.removeAttribute('controls'); } catch (e) {}
        var holder = v.closest('.swiper-slide');
        var play = holder && holder.querySelector('.video-play-button');
        if (play) play.classList.remove('is-hidden');
      });
    }

    function paint(animate) {
      track.style.transition = animate === false || reduceMotion ? 'none' : '';
      track.style.transform = 'translate3d(' + (-index * slideWidth()) + 'px, 0, 0)';

      slides.forEach(function (s, i) {
        var active = i === index;
        s.classList.toggle('is-active', active);
        s.classList.toggle('swiper-slide-active', active);
        s.classList.toggle('swiper-slide-visible', active);
        s.classList.toggle('swiper-slide-fully-visible', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      bullets.forEach(function (b, i) {
        var on = i === index;
        b.classList.toggle('swiper-pagination-bullet-active', on);
        b.setAttribute('aria-current', on ? 'true' : 'false');
      });

      if (prevBtn) {
        prevBtn.classList.toggle('swiper-button-disabled', index === 0);
        prevBtn.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.classList.toggle('swiper-button-disabled', index === slides.length - 1);
        nextBtn.setAttribute('aria-disabled', index === slides.length - 1 ? 'true' : 'false');
      }
    }

    function go(i, animate) {
      var next = Math.max(0, Math.min(slides.length - 1, i));
      if (next !== index) stopVideos(null);
      index = next;
      paint(animate);
    }

    function activate(el, handler) {
      if (!el) return;
      el.addEventListener('click', handler);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          handler(e);
        }
      });
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    }

    activate(prevBtn, function () { go(index - 1); });
    activate(nextBtn, function () { go(index + 1); });
    bullets.forEach(function (b, i) { activate(b, function () { go(i); }); });

    // Play buttons
    each(root.querySelectorAll('.video-play-button'), function (btn) {
      activate(btn, function () {
        var holder = btn.closest('.swiper-slide');
        var video = holder && holder.querySelector('video');
        if (!video) return;
        stopVideos(video);
        video.setAttribute('controls', 'controls');
        video.setAttribute('controlsList', 'nodownload');
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
        btn.classList.add('is-hidden');
      });
    });

    each(root.querySelectorAll('video'), function (v) {
      v.addEventListener('pause', function () {
        var holder = v.closest('.swiper-slide');
        var play = holder && holder.querySelector('.video-play-button');
        if (play && v.currentTime > 0 && !v.ended) return; // scrubbing, keep hidden
        if (play) play.classList.remove('is-hidden');
      });
      v.addEventListener('ended', function () {
        var holder = v.closest('.swiper-slide');
        var play = holder && holder.querySelector('.video-play-button');
        if (play) play.classList.remove('is-hidden');
        v.removeAttribute('controls');
      });
    });

    // Touch / pointer swipe
    var startX = null;
    var startY = null;
    var dragging = false;

    frame.addEventListener('touchstart', function (e) {
      if (!e.touches || e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dragging = false;
    }, { passive: true });

    frame.addEventListener('touchmove', function (e) {
      if (startX === null || !e.touches || !e.touches.length) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      if (!dragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 12) dragging = true;
    }, { passive: true });

    frame.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var endX = e.changedTouches && e.changedTouches.length ? e.changedTouches[0].clientX : startX;
      var dx = endX - startX;
      if (dragging && Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
      startX = null;
      startY = null;
      dragging = false;
    });

    // Keyboard on the frame itself
    frame.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { paint(false); }, 120);
    });

    paint(false);
  }

  /* ================================================================== *
   * 3. 3D model cards
   * ================================================================== */

  var mvState = 'idle'; // idle | loading | ready | failed
  var mvQueue = [];

  function loadModelViewer(cb) {
    if (window.customElements && window.customElements.get('model-viewer')) {
      mvState = 'ready';
      return cb(true);
    }
    mvQueue.push(cb);
    if (mvState === 'loading') return;
    mvState = 'loading';

    var host = document.querySelector('[data-aw-model-viewer-src]');
    var src = host && host.getAttribute('data-aw-model-viewer-src');
    if (!src) { flushMv(false); return; }

    var tag = document.createElement('script');
    tag.type = 'module';
    tag.src = src;
    tag.onerror = function () { flushMv(false); };
    document.head.appendChild(tag);

    // The element registers itself once the module evaluates.
    var waited = 0;
    (function poll() {
      if (window.customElements && window.customElements.get('model-viewer')) return flushMv(true);
      if ((waited += 150) > 25000) return flushMv(false);
      setTimeout(poll, 150);
    })();
  }

  function flushMv(ok) {
    mvState = ok ? 'ready' : 'failed';
    var q = mvQueue.slice();
    mvQueue.length = 0;
    q.forEach(function (cb) { cb(ok); });
  }

  function initModelCard(card) {
    if (card.getAttribute('data-aw-wired') === 'true') return;
    card.setAttribute('data-aw-wired', 'true');

    var viewer = card.querySelector('model-viewer');
    if (!viewer) return;

    function open() {
      if (card.classList.contains('is-live') || card.classList.contains('is-loading')) return;
      card.classList.add('is-loading');
      loadModelViewer(function (ok) {
        card.classList.remove('is-loading');
        if (!ok) {
          // Leave the poster in place - the card still shows the product photo
          // and its link. Nothing worse than before.
          card.classList.add('is-unavailable');
          return;
        }
        card.classList.add('is-live');
        // The element upgraded in place; reveal it.
        if (typeof viewer.dismissPoster === 'function') {
          viewer.dismissPoster();
        } else {
          viewer.setAttribute('reveal', 'auto');
        }
      });
    }

    each(card.querySelectorAll('.aw-model-poster, .aw-model-badge, #lazy-load-poster, #button-3d'), function (el) {
      el.addEventListener('click', open);
    });
    card.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        if (e.target && e.target.closest && e.target.closest('a')) return;
        e.preventDefault();
        open();
      }
    });
  }

  /* ================================================================== *
   * 4. Missing anchor targets
   *
   * "EXPLORE FAQ" points at #faq, which does not exist on this template -
   * the FAQ is a Shopify section, so its id is template-generated. Rather
   * than hardcode that id (it changes if the template is recreated), drop a
   * real anchor in front of the section. The other three dead fragments
   * (#product, #choose-your-region, #choose-your-reason) are repointed at
   * #aw-d-hero in the template itself, so they need no JS.
   * ================================================================== */

  function addAnchor(id, target) {
    if (!target || document.getElementById(id)) return false;
    var a = document.createElement('span');
    a.id = id;
    a.className = 'aw-anchor';
    a.setAttribute('aria-hidden', 'true');
    target.parentNode.insertBefore(a, target);
    return true;
  }

  function initAnchors() {
    var faq = document.querySelector('[id^="shopify-section-"][id*="faq"]');
    var added = addAnchor('faq', faq);

    // If the page was opened directly on one of these fragments, the browser
    // already gave up scrolling before we created the anchor.
    if (added && window.location.hash) {
      var el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(function () { el.scrollIntoView(); }, 40);
    }
  }

  /* ================================================================== *
   * boot
   * ================================================================== */

  function boot() {
    initAnchors();
    each(document.querySelectorAll('.testimonials-section[data-aw-arrows], .testimonials-section[data-aw-autoplay]'), initTestimonials);
    each(document.querySelectorAll('.del-video-slider'), initVideoSlider);
    each(document.querySelectorAll('.aw-model-card'), initModelCard);
  }

  ready(boot);

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function () { setTimeout(boot, 60); });
})();
